import { useMemo, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { useAppDispatch } from 'hooks/useAppDispatch';

import { PersonalDetailsForm } from './PersonalDetailsForm';
import { Error } from 'components/common/error/Error';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsContent } from 'components/common/ns-content/NsContent';
import { NsForm } from 'components/common/ns-form/NsForm';
import { useValidateEmailThunk } from 'hooks/useEmailValidationThunk';
import { useNavigation } from 'hooks/useNavigation';
import { NsStopgap } from 'components/common/ns-stopgap/NsStopgap';
import { EnergySavingFact } from 'components/common/energy-saving-fact/EnergySavingFact';
import { NsCard } from 'components/common/ns-card/NsCard';

import {
  setAccountField,
  selectDetails as selectAccountDetails,
  selectErrors as selectEmailVerificationErrors,
  selectIsLoading as selectEmailVerificationInProgress,
  slice as createAccountSlice
} from 'state/account/account-slice';
import {
  setMarketingPreferences,
  selectMarketingPreferences
} from 'state/user-preferences/user-preferences-slice';
import { getPhoneType } from 'utils/fieldValidations';

export const personalDetailsTestId = 'personalDetailsTestId';

export const PersonalDetailsContent = () => {
  const dispatch = useAppDispatch();
  const account = useSelector(selectAccountDetails, shallowEqual);
  const { email } = account;
  const emailVerificationErrors = useSelector(
    selectEmailVerificationErrors,
    shallowEqual
  );
  const emailVerificationInProgress = useSelector(
    selectEmailVerificationInProgress,
    shallowEqual
  );
  const marketingPreferences = useSelector(
    selectMarketingPreferences,
    shallowEqual
  );
  const validateEmailThunk = useValidateEmailThunk(createAccountSlice);
  const { eventTypes, next } = useNavigation();

  const handleGoNext = useCallback(
    async (e, formData) => {
      e.preventDefault();
      const formValue = formData.fieldsByName['marketing-consent'].value;
      const formValuesArr = formValue ? formValue.split(',') : [];
      const marketingPrefs = Object.keys(marketingPreferences).reduce(
        (acc, key) => {
          return {
            ...acc,
            [key]: formValuesArr.includes(key)
          };
        },
        {}
      );
      dispatch(setMarketingPreferences(marketingPrefs));
      try {
        !!account.email && (await dispatch(validateEmailThunk(email)));
        next({ eventType: eventTypes.NEXT });
      } catch (e) {
        e && console.error(e);
      }
    },
    [
      dispatch,
      validateEmailThunk,
      email,
      next,
      eventTypes.NEXT,
      account.email,
      marketingPreferences
    ]
  );

  const handleChange = useCallback(
    (event) => {
      const {
        target: { name, processedValue: value }
      } = event;
      dispatch(setAccountField({ value, name }));
      if (name === 'phoneNumber') {
        dispatch(
          setAccountField({ name: 'phoneType', value: getPhoneType(value) })
        );
      }
    },
    [dispatch]
  );

  return useMemo(() => {
    return (
      <div>
        {emailVerificationInProgress && <NsStopgap />}
        <div
          data-testid={personalDetailsTestId}
          hidden={emailVerificationInProgress}
        >
          <NsCard>
            <NsContent spacing="normal">
              <NsForm onSubmit={handleGoNext}>
                <PersonalDetailsForm
                  account={account}
                  handleChange={handleChange}
                />
                <Error errors={emailVerificationErrors ?? []} />
                <NsPrimaryCta isLoading={emailVerificationInProgress}>
                  Continue
                </NsPrimaryCta>
                <EnergySavingFact fact="A smart meter shows your gas and electricity use in real time, helping you spot ways to save money." />
              </NsForm>
            </NsContent>
          </NsCard>
        </div>
      </div>
    );
  }, [
    handleGoNext,
    account,
    emailVerificationErrors,
    emailVerificationInProgress,
    handleChange
  ]);
};

export default PersonalDetailsContent;
